import { FieldValues, FieldPath, Control, RegisterOptions, Controller } from 'react-hook-form';
import { AppCheckboxGroupField, AppCheckboxGroupFieldProps } from '../fields/checkboxes/checkboxes';
import { FieldsWithType } from 'src/helpers/typeUtils';

type HookFormCheckboxGroupFieldProps<
  TOption extends Record<Key, string | number>,
  Key extends FieldsWithType<TOption, string | number>,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<AppCheckboxGroupFieldProps<TOption, Key>, 'value' | 'onChange'> & {
  name: TName;
  control: Control<TFieldValues>;
  rules?: Exclude<RegisterOptions, 'valueAsDate' | 'setValueAs'>;
  onFocus?: () => void;
  defaultValue?: unknown;
};

export function HookFormCheckboxGroupField<
  TOption extends Record<Key, string | number>,
  Key extends FieldsWithType<TOption, string | number>,
>(props: HookFormCheckboxGroupFieldProps<TOption, Key>) {
  const { control, name, rules, ...rest } = props;

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      defaultValue={props.defaultValue}
      render={({ field: { onChange, value, ref }, fieldState: { error }, formState: { isSubmitting } }) => (
        <div tabIndex={0} ref={ref}>
          <AppCheckboxGroupField<TOption, Key>
            {...rest}
            errorProps={{
              ...props.errorProps,
              errors: error?.message ?? props.errorProps?.errors,
            }}
            disabled={props.disabled || isSubmitting}
            value={value || []}
            onChange={onChange}
          />
        </div>
      )}
    />
  );
}
